import { z } from 'zod'
import { orderResponseSchema } from './order-response.schema'
import {
  dayTradeBotEventSchema,
  dayTradeBotInstrumentResponseSchema,
  dayTradeBotSettingsSchema,
} from './day-trade-bot.schema'
import { dayTradeBotStatsResponseSchema } from './day-trade-stats.schema'

// the schema the account-level debug export is stamped with; bump when the shape changes so an
// offline reader (or the analyze-bot-data command) can detect and adapt to an older capsule.
// v2 (2026-08-20) added commissionRate + the signed todayTaxBase/allTimeTaxBase to statInputs, so
// the pooled per-instrument tax can be reproduced offline. they are OPTIONAL rather than required:
// exports are files that live on disk and get re-opened long after they were written, so a v1
// export must keep parsing. read the version to know whether to expect them.
export const DAY_TRADE_ACCOUNT_DEBUG_SCHEMA_VERSION = 2

// one executed fill that feeds the realized-P&L recompute, normalized to a per-share price + lot
// count. these are the exact inputs computeDayTradePnlBreakdown replays, so the gross/commission/
// tax/net figures can be recomputed and verified offline.
export const dayTradePnlFillSchema = z.object({
  side: z.enum(['BUY', 'SELL']),
  price: z.number(),
  lots: z.number().int(),
  date: z.coerce.date(),
})
export type DayTradePnlFillDto = z.infer<typeof dayTradePnlFillSchema>

// the raw per-instrument inputs the aggregate statistics are derived from. paired with the
// account-level taxRatePercent + todayStart in the meta, this is everything needed to independently
// recompute the stats and pinpoint where a calculation diverges.
export const dayTradeInstrumentStatInputsSchema = z.object({
  instrumentUid: z.string(),
  ticker: z.string().nullable(),
  // shares per lot used to scale lots into shares
  lotSize: z.number(),
  // actual broker commission attributed to the all-time / today windows (positive cost). this is
  // the DISPLAYED commission line; it is account-wide for the instrument and so may include manual
  // trades that `fills` (day-trade-bot only) does not contain.
  commissionAllTime: z.number(),
  commissionToday: z.number(),
  // configured commission rate the per-fill (tax-deductible) commission is attributed with.
  // absent in a v1 export.
  commissionRate: z.number().optional(),
  // the SIGNED, unfloored tax bases per window. the per-instrument tax in `stats` is an account-
  // POOLED allocation (base x rate, gated on the sign of the account-wide sum), not a local floor,
  // so it cannot be reproduced from `fills` alone — these make the pooling verifiable offline.
  // absent in a v1 export.
  todayTaxBase: z.number().optional(),
  allTimeTaxBase: z.number().optional(),
  fills: z.array(dayTradePnlFillSchema),
})
export type DayTradeInstrumentStatInputsDto = z.infer<typeof dayTradeInstrumentStatInputsSchema>

export const dayTradeBotAccountDebugMetaSchema = z.object({
  schemaVersion: z.number().int(),
  bot: z.literal('day-trade'),
  account: z.object({ connectionId: z.string(), accountId: z.string() }),
  exportedAt: z.coerce.date(),
  // UTC day boundary the today vs all-time windows are split on (matches the engine's daily reset)
  todayStart: z.coerce.date(),
  // dividend-tax rate the figures were computed with, as a percent (e.g. 13 for 13%)
  taxRatePercent: z.number(),
  instrumentCount: z.number().int(),
  orderCount: z.number().int(),
  eventCount: z.number().int(),
})
export type DayTradeBotAccountDebugMetaDto = z.infer<typeof dayTradeBotAccountDebugMetaSchema>

// a self-contained, account-level, all-time debug capsule for the day-trade bot: the config, the
// computed statistics, the raw inputs those statistics derive from, the full order ledger, and the
// complete event history — bundled for one-click download and offline analysis.
export const dayTradeBotAccountDebugResponseSchema = z.object({
  meta: dayTradeBotAccountDebugMetaSchema,
  settings: dayTradeBotSettingsSchema.nullable(),
  instruments: z.array(dayTradeBotInstrumentResponseSchema),
  stats: dayTradeBotStatsResponseSchema,
  statInputs: z.array(dayTradeInstrumentStatInputsSchema),
  orders: z.array(orderResponseSchema),
  events: z.array(dayTradeBotEventSchema),
})
export type DayTradeBotAccountDebugResponseDto = z.infer<
  typeof dayTradeBotAccountDebugResponseSchema
>
